import React, { FC, useCallback, useState } from 'react';

import { Modal } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PhotoCameraOutlinedIcon from '@mui/icons-material/PhotoCameraOutlined';
import RefreshIcon from '@mui/icons-material/Refresh';
import RemoveRedEyeOutlinedIcon from '@mui/icons-material/RemoveRedEyeOutlined';
import { toast } from 'react-toastify';

import { Button } from '@studykik/ui-library';
import { useTranslation } from 'react-i18next';
import { toBase64 } from '../../../utils/files';

import {
  PaymentSubmissionFieldContainer,
  PaymenSubmissionFieldLabel,
  PaymenSubmissionFieldWrapper,
} from '../styles';
import {
  ImagePreview,
  ImagePreviewWrapper,
  ImagePreviewWrapperActions,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHead,
  ModalHeadCTA,
  ModalHeadTitle,
  ModalImage,
  ReceiptButton,
  ReceiptButtonInput,
  ReceiptButtonLabel,
} from './styles';

/** Megabytes * 1000000 (byte) */
const MAX_FILE_SIZE = 10 * 1000000;

export interface IAddReceiptField {
  onChange: (b64Image: string) => void;
  value?: string;
}

export const AddReceiptField: FC<IAddReceiptField> = (props) => {
  const { onChange, value } = props;

  const [tmpValue, setTmpValue] = useState<string>();

  const { t } = useTranslation();

  const handleInputChange = useCallback(
    async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target?.files?.[0];

      if (!file) {
        return;
      }

      if (file.size > MAX_FILE_SIZE) {
        toast.error('Image exceeds 10mb limit');
        return;
      }

      setTmpValue(await toBase64(file));
    },
    []
  );

  const handleUseClick = useCallback(() => {
    if (!tmpValue) {
      return;
    }

    onChange(tmpValue);
    setTmpValue('');
  }, [tmpValue]);

  return (
    <>
      <PaymentSubmissionFieldContainer>
        <PaymenSubmissionFieldLabel>
          {t('Add Receipt')}
        </PaymenSubmissionFieldLabel>
        <PaymenSubmissionFieldWrapper>
          {!value ? (
            <ReceiptButton>
              <PhotoCameraOutlinedIcon fontSize="small" />
              <ReceiptButtonLabel>{t('Add Image')}</ReceiptButtonLabel>
              <ReceiptButtonInput
                type="file"
                accept=".png,.jpg,.jpeg"
                onChange={handleInputChange}
              />
            </ReceiptButton>
          ) : (
            <ImagePreviewWrapper>
              <ImagePreview src={value} />
              <ImagePreviewWrapperActions>
                <Button
                  variant="text"
                  color="secondary"
                  startIcon={<RefreshIcon />}
                >
                  {t('Retake')}
                  <ReceiptButtonInput
                    type="file"
                    accept=".png,.jpg,.jpeg"
                    capture="environment"
                    onChange={handleInputChange}
                  />
                </Button>
                <Button
                  variant="text"
                  color="secondary"
                  startIcon={<RemoveRedEyeOutlinedIcon />}
                  onClick={() => setTmpValue(value)}
                >
                  {t('View')}
                </Button>
              </ImagePreviewWrapperActions>
            </ImagePreviewWrapper>
          )}
        </PaymenSubmissionFieldWrapper>
      </PaymentSubmissionFieldContainer>

      <Modal open={!!tmpValue}>
        <ModalBody>
          <ModalHead>
            <ModalHeadCTA />
            <ModalHeadTitle>{t('Add Receipt Image')}</ModalHeadTitle>
            <ModalHeadCTA
              clickable
              onClick={() => setTmpValue('')}
              role="button"
            >
              <CloseIcon />
            </ModalHeadCTA>
          </ModalHead>
          <ModalContent>
            <ModalImage src={tmpValue} />
          </ModalContent>
          <ModalFooter>
            <Button size="large" variant="outlined">
              {t('Retake/Replace')}
              <ReceiptButtonInput
                type="file"
                accept=".png,.jpg,.jpeg"
                capture="environment"
                onChange={handleInputChange}
              />
            </Button>
            <Button size="large" onClick={handleUseClick}>
              {t('Use')}
            </Button>
          </ModalFooter>
        </ModalBody>
      </Modal>
    </>
  );
};
